import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";
import { Plus, Users, Copy, CheckCircle, Pencil, Check, X, Eye, Trash2, MapPin } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function TeamsManagement({ teams, gameId, gameStatus, onCreateTeam, onRefresh }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);
  const [joinCodes, setJoinCodes] = useState({});
  const [hubNames, setHubNames] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (gameId) {
      axios.get(`${API}/games/${gameId}/team-join-codes`)
        .then(res => {
          const map = {};
          (res.data || []).forEach(t => { map[t.id] = t.join_code; });
          setJoinCodes(map);
        })
        .catch(() => {});
      axios.get(`${API}/games/${gameId}/hubs`)
        .then(res => {
          const map = {};
          (res.data || []).forEach(h => { map[h.code] = h.name; });
          setHubNames(map);
        })
        .catch(() => {});
    }
  }, [gameId, teams?.length]);  

  const copyToClipboard = (text, id) => {
    const fallback = () => {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(fallback);
      } else {
        fallback();
      }
    } catch {
      fallback();
    }
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard");
  };

  const startEditing = (team) => {
    setEditingId(team.id);
    setEditName(team.name);
    setEditCode(team.airline_code);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditName("");
    setEditCode("");
  };

  const saveEdit = async (team) => {
    if (!editName.trim() || !editCode.trim()) {
      toast.error("Name and airline code are required");
      return;
    }
    if (editCode.trim().length < 2) {
      toast.error("Airline code must be 2-3 characters");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/games/${team.game_id}/teams/${team.id}`, {
        name: editName.trim(),
        airline_code: editCode.trim()
      });
      toast.success("Team updated");
      setEditingId(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update team");
    } finally {
      setSaving(false);
    }
  };

  const isSetup = gameStatus === "setup";

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await axios.delete(`${API}/games/${gameId}/teams/${deleteTarget.id}`);
      toast.success(t("instructor.teamDeleted", { name: res.data.team_name || deleteTarget.name }));
      setDeleteTarget(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("instructor.teamDeleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardHeader className="border-b border-slate-100 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="font-heading">{t("nav.teams")}</CardTitle>
          <CardDescription>Manage teams and view join codes</CardDescription>
        </div>
        {isSetup && (
          <Button onClick={onCreateTeam} className="gap-2" data-testid="add-team-btn">
            <Plus className="w-4 h-4" />
            Add Team
          </Button>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {teams.length === 0 ? (
          <div className="py-12 text-center">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">{t("instructor.noTeams")}</p>
            <p className="text-sm text-slate-400">Create teams and share join codes with students</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("dashboard.airline")}</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("teamCollab.members")}</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("instructor.hub")}</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("dashboard.cash")}</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("dashboard.stockPrice")}</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">{t("instructor.joinCode")}</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">ID</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => (
                <tr key={team.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`team-row-${team.id}`}>
                  <td className="py-3 px-4">
                    {editingId === team.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8 w-40 text-sm"
                          data-testid="edit-team-name-input"
                        />
                        <Input
                          value={editCode}
                          onChange={(e) => setEditCode(e.target.value.toUpperCase())}
                          className="h-8 w-16 text-sm font-mono"
                          maxLength={3}
                          data-testid="edit-airline-code-input"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-900">{team.name}</span>
                        <span className="text-xs text-slate-400 font-mono">{team.airline_code}</span>
                        {team.members?.length === 0 && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">AI</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-600">
                    {team.members?.length || 0} students
                  </td>
                  <td className="py-3 px-4" data-testid={`team-hub-${team.id}`}>
                    {team.hub_id ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                        <MapPin className="w-3.5 h-3.5 text-sky-500" />
                        {hubNames[team.hub_id] || team.hub_id}
                        <span className="text-xs text-slate-400 font-mono">{team.hub_id}</span>
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">{t("instructor.noHub")}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-slate-700">
                    {formatCurrency(team.cash)}
                  </td>
                  <td className="py-3 px-4 text-right font-mono text-slate-700">
                    {formatCurrency(team.stock_price, 2)}
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => copyToClipboard(joinCodes[team.id] || "N/A", `code-${team.id}`)}
                      className="flex items-center gap-2 px-2 py-1 bg-slate-100 rounded hover:bg-slate-200 transition-colors"
                      data-testid={`copy-join-code-${team.id}`}
                    >
                      <span className="font-mono text-sm">{joinCodes[team.id] || "N/A"}</span>
                      {copiedId === `code-${team.id}` ? (
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <Copy className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </td>
                  <td className="py-3 px-4">
                    <button
                      onClick={() => copyToClipboard(team.id, `id-${team.id}`)}
                      className="flex items-center gap-2 px-2 py-1 bg-slate-100 rounded hover:bg-slate-200 transition-colors"
                      data-testid={`copy-team-id-${team.id}`}
                    >
                      <span className="font-mono text-xs text-slate-500 max-w-[100px] truncate">{team.id}</span>
                      {copiedId === `id-${team.id}` ? (
                        <CheckCircle className="w-3 h-3 text-emerald-500" />
                      ) : (
                        <Copy className="w-3 h-3 text-slate-400" />
                      )}
                    </button>
                  </td>
                  {isSetup && (
                    <td className="py-3 px-2">
                      {editingId === team.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => saveEdit(team)}
                            disabled={saving}
                            className="p-1.5 rounded hover:bg-emerald-100 text-emerald-600 transition-colors"
                            data-testid={`save-edit-${team.id}`}
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="p-1.5 rounded hover:bg-red-100 text-red-500 transition-colors"
                            data-testid={`cancel-edit-${team.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/instructor/view-team/${gameId}/${team.id}`)}
                            className="p-1.5 rounded hover:bg-sky-100 text-slate-400 hover:text-sky-600 transition-colors"
                            data-testid={`view-team-${team.id}`}
                            title="View student panel"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => startEditing(team)}
                            className="p-1.5 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                            data-testid={`edit-team-${team.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(team)}
                            className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                            data-testid={`delete-team-${team.id}`}
                            title={t("instructor.deleteTeam")}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                  {!isSetup && (
                    <td className="py-3 px-2">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/instructor/view-team/${gameId}/${team.id}`)}
                          className="p-1.5 rounded hover:bg-sky-100 text-slate-400 hover:text-sky-600 transition-colors"
                          data-testid={`view-team-${team.id}`}
                          title="View student panel"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(team)}
                          className="p-1.5 rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors"
                          data-testid={`delete-team-${team.id}`}
                          title={t("instructor.deleteTeam")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-team-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("instructor.deleteTeamTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("instructor.deleteTeamDesc", {
                name: deleteTarget?.name || "",
                count: deleteTarget?.members?.length || 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-team-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-team-confirm"
            >
              {deleting ? "..." : t("instructor.deleteTeam")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
